// Data hooks. Every change invalidates all queries, so an approval made anywhere clears it everywhere
// (the Assistant, Requests, Notifications and the sidebar badges all refetch).
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ActivityType, BriefKind, ContextTargetKind, Page, UserPrefs } from '@dpw/shared';
import { api } from '../api';

/** Follow the cursor to the end, so a list is never silently cut off (a page is up to 100 rows). */
export async function allPages<T>(fetch: (cursor?: string) => Promise<Page<T>>, maxPages = 50): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < maxPages; i++) {
    const p = await fetch(cursor);
    out.push(...p.items);
    if (!p.nextCursor) return out;
    cursor = p.nextCursor;
  }
  throw new Error('This list is longer than the app can show at once. Narrow it with a filter.');
}

/** enabled false on the sign-in pages: no session check there (it would only fail). */
export const useMe = (enabled = true) => useQuery({ queryKey: ['me'], queryFn: () => api.auth.me().then(r => r.user), retry: false, staleTime: 60_000, enabled });
export const useSummary = () => useQuery({ queryKey: ['summary'], queryFn: api.summary, refetchInterval: 30_000 });
export const useSpaces = () => useQuery({ queryKey: ['spaces'], queryFn: api.spaces.list });
export const useSpace = (id: string) => useQuery({ queryKey: ['space', id], queryFn: () => api.spaces.get(id), enabled: !!id });
export const useSpaceActivities = (id: string, q: { trackId?: string; type?: ActivityType; mine?: boolean; includeDone?: boolean; clientRequests?: boolean }) =>
  useQuery({ queryKey: ['space-acts', id, q], queryFn: () => allPages(c => api.spaces.activities(id, { ...q, cursor: c })) });
export const useTrack = (id: string) => useQuery({ queryKey: ['track', id], queryFn: () => api.tracks.get(id) });
export const useTrackActivities = (id: string, includeDone: boolean) => useQuery({ queryKey: ['track-acts', id, includeDone], queryFn: () => allPages(c => api.tracks.activities(id, { includeDone, cursor: c })) });
export const useTrackFiles = (id: string) => useQuery({ queryKey: ['track-files', id], queryFn: () => api.tracks.files(id) });
export const useActivity = (id: string, enabled = true) => useQuery({ queryKey: ['activity', id], queryFn: () => api.activities.get(id), enabled });
export const useActivityFiles = (id: string, enabled = true) => useQuery({ queryKey: ['activity-files', id], queryFn: () => api.activities.files(id), enabled });
/** forWhom everyone: every person's cards (the director, space.all). */
export const useActions = (status: 'waiting' | 'all' = 'waiting', forWhom: 'me' | 'everyone' = 'me') =>
  useQuery({ queryKey: ['actions', status, forWhom], queryFn: () => allPages(c => api.actions.list(status, c, forWhom)) });
/** Every open notification comes in the first page; dismissed history loads a page at a time ("Show older"). */
export const useNotifications = () => useInfiniteQuery({
  queryKey: ['notifications'], initialPageParam: undefined as string | undefined, refetchInterval: 30_000,
  queryFn: ({ pageParam }) => api.notifications.list(pageParam),
  getNextPageParam: last => last.nextCursor ?? undefined,
});
export const useReminders = (scope: 'mine' | 'set_by_me' = 'mine') => useQuery({ queryKey: ['reminders', scope], queryFn: () => api.reminders.list(scope) });
export const useCalendar = (from: string, to: string, spaceId?: string, mine?: boolean) =>
  useQuery({ queryKey: ['calendar', from, to, spaceId, mine], queryFn: () => api.calendar({ from, to, spaceId, mine: mine ? 'true' : undefined }) });
export const useConversations = () => useQuery({ queryKey: ['conversations'], queryFn: () => allPages(c => api.assistant.conversations(c)) });
export const useConversation = (id: string | undefined) => useQuery({ queryKey: ['conversation', id], queryFn: () => api.assistant.get(id!), enabled: !!id });
export const useChecks = () => useQuery({ queryKey: ['checks'], queryFn: () => allPages(c => api.checks.list('all', c)) });
export const useCheck = (id: string) => useQuery({ queryKey: ['check', id], queryFn: () => api.checks.get(id) });
export const useUsers = () => useQuery({ queryKey: ['users'], queryFn: api.users.list, staleTime: 60_000 });
export const useContextDoc = (kind: ContextTargetKind, id: string, enabled = true) =>
  useQuery({ queryKey: ['context', kind, id], queryFn: () => api.context.get(kind, id), enabled: enabled && !!id });
export const useContextHistory = (kind: ContextTargetKind, id: string, enabled: boolean) =>
  useQuery({ queryKey: ['context-history', kind, id], queryFn: () => allPages(c => api.context.history(kind, id, c)), enabled });
export const useTeammates = () => useQuery({ queryKey: ['teammates'], queryFn: api.users.teammates, staleTime: 60_000 });
/** Open activities you can see, for "Linked to" pickers. */
export const useOpenActivities = () => useQuery({ queryKey: ['open-activities'], queryFn: () => allPages(c => api.activities.list({ cursor: c, limit: 200 })), staleTime: 30_000 });
export const useSessions = () => useQuery({ queryKey: ['sessions'], queryFn: api.auth.sessions });
/** Email on this server, and the address to forward or copy email to (INBOUND_ADDRESS). */
/** fresh: read it again on opening (Settings lists the emails waiting for a place, which change with every card). */
export const useEmailStatus = (fresh = false) => useQuery({ queryKey: ['email-status'], queryFn: api.email.status, staleTime: fresh ? 0 : 5 * 60_000 });
/** Voice on this server (ELEVENLABS_API_KEY): Record and Speak need it. */
export const useVoiceStatus = () => useQuery({ queryKey: ['voice-status'], queryFn: api.voice.status, staleTime: 5 * 60_000 });
export const useHealth = () => useQuery({ queryKey: ['health'], queryFn: api.health, refetchInterval: 60_000 });

/** Your settings (channels, quiet hours, open Requests groups), kept on the server so they follow you. */
export const usePrefs = () => useQuery({ queryKey: ['prefs'], queryFn: api.prefs.get, staleTime: 60_000 });
/** Change settings optimistically; put them back if the server says no. */
export function useUpdatePrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: Parameters<typeof api.prefs.update>[0]) => api.prefs.update(p),
    onMutate: async p => {
      await qc.cancelQueries({ queryKey: ['prefs'] });
      const before = qc.getQueryData<UserPrefs>(['prefs']);
      if (before) {
        const n = before.notifications;
        const channels = { ...n.channels };
        for (const [k, v] of Object.entries(p.notifications?.channels ?? {}) as [keyof typeof channels, { push?: boolean; email?: boolean } | undefined][]) {
          if (v) channels[k] = { ...channels[k], ...v };
        }
        qc.setQueryData<UserPrefs>(['prefs'], {
          notifications: { channels, quietHours: p.notifications?.quietHours !== undefined ? p.notifications.quietHours : n.quietHours },
          openGroups: p.openGroups ?? before.openGroups,
          voice: p.voice ?? before.voice,
          morningEmail: p.morningEmail ?? before.morningEmail,
        });
      }
      return { before };
    },
    onError: (_e, _p, ctx) => { if (ctx?.before) qc.setQueryData(['prefs'], ctx.before); },
    onSuccess: p => qc.setQueryData(['prefs'], p),
  });
}
export const useStarters = (spaceId?: string) => useQuery({ queryKey: ['starters', spaceId ?? null], queryFn: () => api.assistant.starters(spaceId), staleTime: 5 * 60_000 });
/** The assistant's brief; while a fresh one is being written, look again every few seconds. */
export const useBrief = (kind: BriefKind, id: string) => useQuery({
  queryKey: ['brief', kind, id], queryFn: () => api.brief(kind, id), enabled: !!id,
  refetchInterval: q => (q.state.data?.stale ? 3000 : false),
});
/** Today: the day, the week, Yours and Gone quiet (reminders and cards come from their own lists). */
export const useToday = () => useQuery({ queryKey: ['today'], queryFn: api.today.get });
export const useTrackOverview = (id: string) => useQuery({ queryKey: ['track-overview', id], queryFn: () => api.trackOverview(id), enabled: !!id });
export const useTrackUpdates = (id: string, kind: 'all' | 'minutes' | 'email', enabled = true) =>
  useQuery({ queryKey: ['track-updates', id, kind], queryFn: () => allPages(c => api.trackUpdates(id, kind, c)), enabled: enabled && !!id });
/** Team checks waiting for your answer (the questions in the assistant's "Waiting for you"). */
export const useChecksToAnswer = () => useQuery({ queryKey: ['checks', 'to_answer'], queryFn: () => allPages(c => api.checks.list('to_answer', c)) });

/** A change: runs fn, then refreshes everything on screen. */
export function useChange<A, R>(fn: (a: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries() });
}
